import { useState, useEffect, useMemo } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CircleNotch } from '@phosphor-icons/react'
import { orgApi } from '@/api/organizations'
import type { OrganizationDetail, UpdateOrgRequest } from '@/api/organizations'
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges'
import { useToast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

interface OrgSettingsTabProps {
  org: OrganizationDetail
  onOrgUpdated?: () => void
}

export function OrgSettingsTab({ org, onOrgUpdated }: OrgSettingsTabProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  // Form state initialized from org
  const [tier, setTier] = useState(org.subscription_tier)
  const [maxUsers, setMaxUsers] = useState(org.max_users)

  // Track initial values for dirty comparison
  const [initialTier, setInitialTier] = useState(org.subscription_tier)
  const [initialMaxUsers, setInitialMaxUsers] = useState(org.max_users)

  // Tier change confirmation
  const [showTierConfirm, setShowTierConfirm] = useState(false)
  const [pendingData, setPendingData] = useState<UpdateOrgRequest | null>(null)

  // Sync form when org data updates (after save)
  useEffect(() => {
    setTier(org.subscription_tier)
    setMaxUsers(org.max_users)
    setInitialTier(org.subscription_tier)
    setInitialMaxUsers(org.max_users)
  }, [org.subscription_tier, org.max_users])

  const isDirty = useMemo(() => {
    return tier !== initialTier || maxUsers !== initialMaxUsers
  }, [tier, maxUsers, initialTier, initialMaxUsers])

  // Navigation guard
  const { blocker } = useUnsavedChanges(isDirty)

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: UpdateOrgRequest) => orgApi.update(org.id, data),
    onSuccess: () => {
      toast({
        title: 'Settings saved',
        description: 'Organization settings have been updated.',
      })
      queryClient.invalidateQueries({
        queryKey: ['organizations', 'detail', org.id],
      })
      queryClient.invalidateQueries({
        queryKey: ['organizations', 'list'],
      })
      // Reset initial values to current
      setInitialTier(tier)
      setInitialMaxUsers(maxUsers)
      onOrgUpdated?.()
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to save settings',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  function handleSave() {
    const data: UpdateOrgRequest = {}
    if (tier !== initialTier) data.subscription_tier = tier
    if (maxUsers !== initialMaxUsers) data.max_users = maxUsers

    // If tier changed, show confirmation first
    if (tier !== initialTier) {
      setPendingData(data)
      setShowTierConfirm(true)
      return
    }

    updateMutation.mutate(data)
  }

  function handleConfirmTierChange() {
    if (pendingData) {
      updateMutation.mutate(pendingData)
      setPendingData(null)
    }
    setShowTierConfirm(false)
  }

  function handleDiscard() {
    setTier(initialTier)
    setMaxUsers(initialMaxUsers)
  }

  const settingsJson = Object.keys(org.settings).length > 0
    ? JSON.stringify(org.settings, null, 2)
    : null

  return (
    <div className="space-y-6">
      {/* Settings form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Subscription Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="settings-tier">Subscription Tier</Label>
              <Select value={tier} onValueChange={setTier}>
                <SelectTrigger id="settings-tier">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="settings-max-users">Max Users</Label>
              <Input
                id="settings-max-users"
                type="number"
                min={1}
                max={10000}
                value={maxUsers}
                onChange={(e) => setMaxUsers(Number(e.target.value))}
              />
            </div>
          </div>

          <div className="flex items-center gap-3 pt-4 border-t">
            <Button
              onClick={handleSave}
              disabled={!isDirty || updateMutation.isPending}
            >
              {updateMutation.isPending && (
                <CircleNotch className="h-4 w-4 animate-spin" />
              )}
              Save Changes
            </Button>
            <Button
              variant="outline"
              onClick={handleDiscard}
              disabled={!isDirty}
            >
              Discard Changes
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Organization Settings (JSONB) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Organization Settings</CardTitle>
        </CardHeader>
        <CardContent>
          {settingsJson ? (
            <pre className="rounded-md bg-muted p-4 text-sm font-mono overflow-x-auto">
              {settingsJson}
            </pre>
          ) : (
            <p className="text-sm text-muted-foreground">
              No custom settings configured.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Tier change confirmation dialog */}
      <AlertDialog open={showTierConfirm} onOpenChange={setShowTierConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change subscription tier?</AlertDialogTitle>
            <AlertDialogDescription>
              Change subscription tier from{' '}
              <span className="font-medium capitalize">{initialTier}</span> to{' '}
              <span className="font-medium capitalize">{tier}</span>?
              This may affect available features for all users.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowTierConfirm(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmTierChange}>
              Confirm Change
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Unsaved changes navigation dialog */}
      <AlertDialog open={blocker.state === 'blocked'}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes to organization settings. Are you sure you
              want to leave this page? Your changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => blocker.reset?.()}>
              Stay on page
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => blocker.proceed?.()}>
              Leave page
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
